import { create } from "zustand";

import type { PendingUpload, PromptPrefill } from "./types";

/**
 * Ключ черновика на пустом экране: чата ещё нет, а текст набирают уже сейчас.
 * При отправке текст переезжает на созданный чат (`moveDraftText`), иначе он
 * остался бы под этим ключом и всплыл бы в следующем новом чате.
 */
export const NEW_CHAT_DRAFT_KEY = "__new_chat__";

export interface DraftChatState {
    draftByWorkspace: Record<string, string>;
    setDraft: (workspaceId: string, chatId: string) => void;
    clearDraft: (workspaceId: string) => void;
    dropChatFromDrafts: (chatId: string) => void;
    getDraft: (workspaceId: string) => string | undefined;

    // Текст черновика — chat-scoped, как вложения и элементы. В локальном стейте
    // промпт-бара он не выживал: отправка из НОВОГО чата уводит на `/chat/<id>`,
    // промпт-бар размонтируется вместе с пустым экраном, и набранное пропадало —
    // при том что вложения (они уже были в сторе) оставались.
    textByChat: Record<string, string>;
    setDraftText: (chatId: string, text: string) => void;
    clearDraftText: (chatId: string) => void;
    getDraftText: (chatId: string) => string;
    /** Переносит набранный текст на чат, созданный при отправке из пустого экрана. */
    moveDraftText: (fromChatId: string, toChatId: string) => void;

    pendingByChat: Record<string, PendingUpload[]>;
    addPending: (chatId: string, items: PendingUpload[]) => void;
    updatePending: (chatId: string, localId: string, patch: Partial<PendingUpload>) => void;
    removePending: (chatId: string, localId: string) => void;
    reorderPending: (chatId: string, fromIdx: number, toIdx: number) => void;
    clearPending: (chatId: string) => void;
    getPending: (chatId: string) => PendingUpload[];

    // Выбранные из библиотеки элементы — chat-scoped, как и pending:
    // это часть черновика промпта, а не настройка модели. Уходят в
    // `referenceElementIds` при старте генерации.
    elementIdsByChat: Record<string, string[]>;
    setElementIds: (chatId: string, elementIds: string[]) => void;
    clearElementIds: (chatId: string) => void;
    getElementIds: (chatId: string) => string[];

    // Заявка «повторить запрос» — канал между действием на
    // сообщении и промпт-баром: chat-scoped и одноразовая (промпт-бар снимает
    // её, применив или отклонив). Держим здесь, а не в отдельном сторе, чтобы
    // черновик чата (текст/вложения/элементы/заявка) жил в одном месте.
    prefillByChat: Record<string, PromptPrefill>;
    requestPrefill: (chatId: string, prefill: PromptPrefill) => void;
    clearPrefill: (chatId: string) => void;
    getPrefill: (chatId: string) => PromptPrefill | undefined;
}

export const useDraftChatStore = create<DraftChatState>((set, get) => ({
    draftByWorkspace: {},
    setDraft: (workspaceId, chatId) =>
        set((state) => ({
            draftByWorkspace: { ...state.draftByWorkspace, [workspaceId]: chatId },
        })),
    clearDraft: (workspaceId) =>
        set((state) => {
            const { [workspaceId]: _removed, ...rest } = state.draftByWorkspace;
            return { draftByWorkspace: rest };
        }),
    // Промоутит чат-черновик в живой: убирает его из draft-маппинга, НО
    // намеренно НЕ трогает pending-вложения (и их preview-URL) — после отправки
    // промпта текст и вложения остаются в композере для быстрой повторной
    // генерации. Полная очистка вложений — clearPending.
    dropChatFromDrafts: (chatId) =>
        set((state) => {
            const next: Record<string, string> = {};
            for (const [ws, id] of Object.entries(state.draftByWorkspace)) {
                if (id !== chatId) next[ws] = id;
            }
            return { draftByWorkspace: next };
        }),
    getDraft: (workspaceId) => get().draftByWorkspace[workspaceId],

    textByChat: {},
    setDraftText: (chatId, text) => set((state) => ({ textByChat: { ...state.textByChat, [chatId]: text } })),
    clearDraftText: (chatId) =>
        set((state) => {
            if (!(chatId in state.textByChat)) return state;
            const { [chatId]: _removed, ...rest } = state.textByChat;
            return { textByChat: rest };
        }),
    getDraftText: (chatId) => get().textByChat[chatId] ?? "",
    moveDraftText: (fromChatId, toChatId) =>
        set((state) => {
            if (fromChatId === toChatId) return state;
            const text = state.textByChat[fromChatId];
            if (text === undefined) return state;
            const { [fromChatId]: _moved, ...rest } = state.textByChat;
            return { textByChat: { ...rest, [toChatId]: text } };
        }),

    pendingByChat: {},
    addPending: (chatId, items) =>
        set((state) => ({
            pendingByChat: {
                ...state.pendingByChat,
                [chatId]: [...(state.pendingByChat[chatId] ?? []), ...items],
            },
        })),
    updatePending: (chatId, localId, patch) =>
        set((state) => {
            const list = state.pendingByChat[chatId];
            if (!list) return state;
            const next = list.map((item) => (item.localId === localId ? { ...item, ...patch } : item));
            return { pendingByChat: { ...state.pendingByChat, [chatId]: next } };
        }),
    removePending: (chatId, localId) =>
        set((state) => {
            const list = state.pendingByChat[chatId];
            if (!list) return state;
            const target = list.find((item) => item.localId === localId);
            if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
            const next = list.filter((item) => item.localId !== localId);
            return { pendingByChat: { ...state.pendingByChat, [chatId]: next } };
        }),
    reorderPending: (chatId, fromIdx, toIdx) =>
        set((state) => {
            const list = state.pendingByChat[chatId];
            if (!list || fromIdx === toIdx) return state;
            if (fromIdx < 0 || fromIdx >= list.length || toIdx < 0 || toIdx >= list.length) return state;
            const next = list.slice();
            const [moved] = next.splice(fromIdx, 1);
            if (!moved) return state;
            next.splice(toIdx, 0, moved);
            return { pendingByChat: { ...state.pendingByChat, [chatId]: next } };
        }),
    clearPending: (chatId) =>
        set((state) => {
            const list = state.pendingByChat[chatId];
            if (list) {
                for (const item of list) {
                    if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
                }
            }
            const { [chatId]: _removed, ...rest } = state.pendingByChat;
            return { pendingByChat: rest };
        }),
    getPending: (chatId) => get().pendingByChat[chatId] ?? [],

    elementIdsByChat: {},
    setElementIds: (chatId, elementIds) =>
        set((state) => ({
            elementIdsByChat: { ...state.elementIdsByChat, [chatId]: elementIds },
        })),
    clearElementIds: (chatId) =>
        set((state) => {
            const { [chatId]: _removed, ...rest } = state.elementIdsByChat;
            return { elementIdsByChat: rest };
        }),
    getElementIds: (chatId) => get().elementIdsByChat[chatId] ?? [],

    prefillByChat: {},
    requestPrefill: (chatId, prefill) =>
        set((state) => ({
            prefillByChat: { ...state.prefillByChat, [chatId]: prefill },
        })),
    clearPrefill: (chatId) =>
        set((state) => {
            if (!(chatId in state.prefillByChat)) return state;
            const { [chatId]: _removed, ...rest } = state.prefillByChat;
            return { prefillByChat: rest };
        }),
    getPrefill: (chatId) => get().prefillByChat[chatId],
}));
